import type { FC } from 'react';
import React, { useCallback } from 'react';
import Slider from 'react-slick';
import type { BoxProps } from '@withjoy/joykit';
import { Box } from '@withjoy/joykit';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { SliderContainer } from './PhotoSlider.styles';
import type { Photo, BrannanPhotoSliderProps } from './PhotoSlider.types';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { useSliderSettings } from './hooks/useSliderSettings';
import { PHOTO_COUNT_TO_SHOW } from './PhotoSlider.constants';
import { OptimizedEventGuestSitePhoto } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoSlider.brannan?.aspectRatio as number;

const FULL_WIDTH_PROPERTIES: BoxProps = {
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  width: '100vw',
  overflow: 'hidden',
  // All CSS units are converted to values before doing the operation.
  // -50vw: is 50% of the viewport width
  // +50%: is 50% of the *element's* width
  //
  // it's effectively the same as setting `left: -50%; transform: translateX(50%);`
  // but we need to calculate based off the viewport, not the element's width
  left: 'calc(-50vw + 50%)',
};

export const BrannanPhotoSlider: FC<BrannanPhotoSliderProps> = props => {
  const { photos, showPlaceholder, aspectRatio = CONTAINER_ASPECT_RATIO } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, showPlaceholder && photos.length === 0);

  const [containerDimensions, containerRef] = useSizeObserver();
  const { settings, sliderRef } = useSliderSettings();

  const getPhotoComponent = useCallback(
    (photo: Photo) => (
      <Box
        key={photo.id}
        width="100%"
        aspectRatio={`${aspectRatio}`}
        height="auto"
      >
        <OptimizedEventGuestSitePhoto
          photoUrl={photo.photoUrl}
          cropInfo={photo.cropInfo ? { ...photo.cropInfo, unit: '%' } : null}
          width={containerDimensions.width}
          height={containerDimensions.width / aspectRatio}
          containerType="fixed-aspect"
        />
      </Box>
    ),
    [containerDimensions.width, aspectRatio],
  );

  return (
    <SliderContainer
      ref={containerRef}
      height="100%"
      position="relative"
      aspectRatio={filledPhotos.length > 0 ? `${aspectRatio}` : undefined}
      {...FULL_WIDTH_PROPERTIES}
    >
      <Slider
        ref={sliderRef}
        {...settings}
      >
        {filledPhotos.map((photo, index) => {
          return photo.photoUrl ? (
            getPhotoComponent(photo)
          ) : (
            <Box
              key={index}
              width="100%"
              height="auto"
              aspectRatio={`${aspectRatio}`}
              backgroundColor="lightgray"
            />
          );
        })}
      </Slider>
    </SliderContainer>
  );
};

BrannanPhotoSlider.displayName = 'BrannanPhotoSlider';
